import { useCallback } from 'react';
import stringsConfig from './strings.json';

export type StringKey = string;
export type Language = 'es' | 'en';

interface LanguageStrings {
  [key: string]: string;
}

interface StringsData {
  es: LanguageStrings;
  en: LanguageStrings;
  companies?: Record<string, Record<Language, LanguageStrings>>;
}

class StringsService {
  private static instance: StringsService;
  private stringsData: StringsData = stringsConfig as StringsData;
  private currentCompanyId: string | null = null;
  private currentLanguage: Language = 'es';

  private constructor() {
    const saved = localStorage.getItem('app_language');
    if (saved === 'es' || saved === 'en') {
      this.currentLanguage = saved;
    }
  }

  static getInstance(): StringsService {
    if (!StringsService.instance) {
      StringsService.instance = new StringsService();
    }
    return StringsService.instance;
  }

  setCurrentCompany(companyId: string | null) {
    this.currentCompanyId = companyId;
  }

  setLanguage(language: Language) {
    this.currentLanguage = language;
    localStorage.setItem('app_language', language);
    window.location.reload();
  }

  getLanguage(): Language {
    return this.currentLanguage;
  }

  get(key: StringKey): string {
    try {
      const lang = this.currentLanguage;

      // Check company-specific strings first
      if (this.currentCompanyId && this.stringsData.companies?.[this.currentCompanyId]) {
        const companyLangStrings = this.stringsData.companies[this.currentCompanyId][lang];
        if (companyLangStrings && companyLangStrings[key]) {
          return companyLangStrings[key];
        }
      }

      // Check default language strings
      const defaultLangStrings = this.stringsData[lang];
      if (defaultLangStrings && defaultLangStrings[key]) {
        return defaultLangStrings[key];
      }

      // Fallback to Spanish if not found in current language
      if (lang !== 'es') {
        const fallbackStrings = this.stringsData.es;
        if (fallbackStrings && fallbackStrings[key]) {
          return fallbackStrings[key];
        }
      }

      // Key not found in any language. Return empty string so callers
      // using `strings.get(k) || 'fallback'` get the fallback (the prior
      // literal 'undefined' was truthy and silently bypassed all fallbacks,
      // causing visible "undefined" labels in the UI — see ServiceCardTableColumns).
      return '';
    } catch (error: unknown) {
      return '';
    }
  }

  getInLanguage(key: StringKey, language: Language): string {
    const currentLang = this.currentLanguage;
    this.currentLanguage = language;
    const result = this.get(key);
    this.currentLanguage = currentLang;
    return result;
  }

  getAll() {
    const lang = this.currentLanguage;

    let source: LanguageStrings;

    if (this.currentCompanyId && this.stringsData.companies?.[this.currentCompanyId]) {
      source = this.stringsData.companies[this.currentCompanyId][lang] || this.stringsData[lang];
    } else {
      source = this.stringsData[lang];
    }

    return source || {};
  }

  updateCompanyStrings(
    companyId: string,
    language: Language,
    updates: Partial<Record<StringKey, string>>
  ) {
    if (!this.stringsData.companies) {
      this.stringsData.companies = {};
    }
    if (!this.stringsData.companies[companyId]) {
      this.stringsData.companies[companyId] = {
        es: {},
        en: {}
      };
    }

    const cleanedUpdates = Object.fromEntries(
      Object.entries(updates).filter(([_, value]) => value !== undefined)
    ) as Record<StringKey, string>;

    this.stringsData.companies[companyId][language] = {
      ...this.stringsData.companies[companyId][language],
      ...cleanedUpdates,
    };
  }

  getCompanyStrings(companyId: string, language: Language) {
    return this.stringsData.companies?.[companyId]?.[language] || {};
  }

  saveToLocalStorage() {
    try {
      localStorage.setItem('app_strings', JSON.stringify(this.stringsData));
    } catch (error: unknown) {
    }
  }

  loadFromLocalStorage() {
    try {
      const saved = localStorage.getItem('app_strings');
      if (saved) {
        this.stringsData = JSON.parse(saved);
      }
    } catch (error: unknown) {
    }
  }
}

export const stringsService = StringsService.getInstance();

export const getStringsDirect = () => {
  return {
    get: (key: StringKey) => stringsService.get(key),
    getInLanguage: (key: StringKey, language: Language) => stringsService.getInLanguage(key, language),
    setLanguage: (language: Language) => stringsService.setLanguage(language),
    getLanguage: () => stringsService.getLanguage(),
    usuario: stringsService.get('usuario'),
    usuarios: stringsService.get('usuarios'),
    sede: stringsService.get('sede'),
    sedes: stringsService.get('sedes'),
    empresa: stringsService.get('empresa'),
    empresas: stringsService.get('empresas'),
    departamento: stringsService.get('departamento'),
    departamentos: stringsService.get('departamentos'),
    verificacion: stringsService.get('verificacion'),
    verificaciones: stringsService.get('verificaciones'),
    verificacion_global: stringsService.get('verificacion_global'),
    verificaciones_globales: stringsService.get('verificaciones_globales'),
    revision_pendiente: stringsService.get('revision_pendiente'),
    revisiones_pendientes: stringsService.get('revisiones_pendientes'),
    mi_verificacion: stringsService.get('mi_verificacion'),
    mis_verificaciones: stringsService.get('mis_verificaciones'),
    libreria: stringsService.get('libreria'),
    librerias: stringsService.get('librerias'),
    contratista: stringsService.get('contratista'),
    contratistas: stringsService.get('contratistas'),
    curso: stringsService.get('curso'),
    cursos: stringsService.get('cursos'),
    volver: stringsService.get('volver'),
    agregar: stringsService.get('agregar'),
    industria: stringsService.get('industria'),
    empleados: stringsService.get('empleados'),
    acciones: stringsService.get('acciones'),
    usuarios_sin_sede: stringsService.get('usuarios_sin_sede'),
    ver_detalles: stringsService.get('ver_detalles'),
    editar: stringsService.get('editar'),
    eliminar: stringsService.get('eliminar'),
    confirmar_eliminacion: stringsService.get('confirmar_eliminacion'),
    cancelar: stringsService.get('cancelar'),
    cargando: stringsService.get('cargando'),
    no_hay_resultados: stringsService.get('no_hay_resultados'),
    sin_asignar: stringsService.get('sin_asignar'),
    permisos_en_proceso: stringsService.get('permisos_en_proceso'),
    permiso_asignado: stringsService.get('permiso_asignado'),
    permisos_asignados: stringsService.get('permisos_asignados'),
    usuario_admin: stringsService.get('usuario_admin'),
    usuarios_admin: stringsService.get('usuarios_admin'),
  };
};

export const useStrings = () => {
  const get = useCallback((key: StringKey): string => {
    return stringsService.get(key);
  }, []);

  const getInLanguage = useCallback((key: StringKey, language: Language): string => {
    return stringsService.getInLanguage(key, language);
  }, []);

  const setLanguage = useCallback((language: Language) => {
    stringsService.setLanguage(language);
  }, []);

  const getLanguage = useCallback((): Language => {
    return stringsService.getLanguage();
  }, []);

  return {
    get,
    getInLanguage,
    setLanguage,
    getLanguage,
    usuario: get('usuario'),
    usuarios: get('usuarios'),
    sede: get('sede'),
    sedes: get('sedes'),
    empresa: get('empresa'),
    empresas: get('empresas'),
    departamento: get('departamento'),
    departamentos: get('departamentos'),
    verificacion: get('verificacion'),
    verificaciones: get('verificaciones'),
    verificacion_global: get('verificacion_global'),
    verificaciones_globales: get('verificaciones_globales'),
    revision_pendiente: get('revision_pendiente'),
    revisiones_pendientes: get('revisiones_pendientes'),
    mi_verificacion: get('mi_verificacion'),
    mis_verificaciones: get('mis_verificaciones'),
    libreria: get('libreria'),
    librerias: get('librerias'),
    contratista: get('contratista'),
    contratistas: get('contratistas'),
    curso: get('curso'),
    cursos: get('cursos'),
    volver: get('volver'),
    agregar: get('agregar'),
    industria: get('industria'),
    empleados: get('empleados'),
    acciones: get('acciones'),
    usuarios_sin_sede: get('usuarios_sin_sede'),
    ver_detalles: get('ver_detalles'),
    editar: get('editar'),
    eliminar: get('eliminar'),
    confirmar_eliminacion: get('confirmar_eliminacion'),
    cancelar: get('cancelar'),
    cargando: get('cargando'),
    no_hay_resultados: get('no_hay_resultados'),
    sin_asignar: get('sin_asignar'),
    permisos_en_proceso: get('permisos_en_proceso'),
    permiso_asignado: get('permiso_asignado'),
    permisos_asignados: get('permisos_asignados'),
    usuario_admin: get('usuario_admin'),
    usuarios_admin: get('usuarios_admin'),
  };
};
